import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import Loader from '../../layout/Loder';
import { logout, deleteAccount } from '../../actions/userAction';
import './Account.css';

const initialsOf = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Account = () => {
  const { user, loading, isAuthenticated } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isAuthenticated === false) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      setLogoutLoading(true);
      await dispatch(logout());
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Logout failed');
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await dispatch(deleteAccount());
      toast.success('Your account has been deleted.');
      navigate('/');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not delete account. Try again.');
      setDeleting(false);
    }
  };

  if (loading || !user) return <Loader />;

  const joined = user.createdAt ? moment(user.createdAt) : null;
  const dob = user.dob ? moment(user.dob).format('DD MMM YYYY') : null;

  return (
    <>
      <Helmet><title>Account · dmyor</title></Helmet>

      <div className="acc-page">
        <div className="acc-wrap">
          <header className="acc-header">
            <div className="acc-avatar" aria-hidden="true">
              {initialsOf(user.name)}
            </div>
            <div className="acc-identity">
              <h1 className="acc-name">{user.name || 'Unnamed'}</h1>
              <p className="acc-email">{user.email}</p>
              {joined && (
                <p className="acc-joined">
                  On dmyor since {joined.format('MMM YYYY')}
                </p>
              )}
            </div>
          </header>

          <section className="acc-card">
            <div className="acc-card-title">Details</div>

            <div className="acc-row">
              <span className="acc-row-label">Name</span>
              <span className="acc-row-value">{user.name || '—'}</span>
            </div>

            <div className="acc-row">
              <span className="acc-row-label">Email</span>
              <span className="acc-row-value">{user.email}</span>
            </div>

            {dob && (
              <div className="acc-row">
                <span className="acc-row-label">Date of birth</span>
                <span className="acc-row-value">{dob}</span>
              </div>
            )}

            {joined && (
              <div className="acc-row">
                <span className="acc-row-label">Member since</span>
                <span className="acc-row-value">{joined.format('DD MMM YYYY')}</span>
              </div>
            )}
          </section>

          <section className="acc-sub-card">
            <div className="acc-sub-icon" aria-hidden="true">
              <i className="bx bxs-bolt" />
            </div>
            <div className="acc-sub-text">
              <p className="acc-sub-title">dmyor Pro</p>
              <p className="acc-sub-desc">Your free AI requests, plan, billing, and cancellation.</p>
            </div>
            <button type="button" className="acc-manage-btn" onClick={() => navigate('/dashboard/upgrade')}>
              Manage
            </button>
          </section>

          <section className="acc-sub-card">
            <div className="acc-sub-icon" aria-hidden="true">
              <i className="bx bx-envelope" />
            </div>
            <div className="acc-sub-text">
              <p className="acc-sub-title">Contact & support</p>
              <p className="acc-sub-desc">
                Questions, feedback, or a problem? Reach us at{' '}
                <a className="acc-sub-mail" href="mailto:support@dmyor.com">support@dmyor.com</a>.
              </p>
            </div>
            <a className="acc-manage-btn" href="mailto:support@dmyor.com">
              Email us
            </a>
          </section>

          <div className="acc-footer">
            <button
              type="button"
              className="acc-logout"
              onClick={handleLogout}
              disabled={logoutLoading}
            >
              <i className="bx bx-log-out" />
              {logoutLoading ? 'Logging out…' : 'Log out'}
            </button>
          </div>

          <section className="acc-card acc-card--danger">
            <div className="acc-card-title acc-card-title--danger">Danger zone</div>
            <div className="acc-danger-row">
              <div className="acc-danger-text">
                <p className="acc-danger-title">Delete account</p>
                <p className="acc-danger-desc">
                  Permanently deletes your account and all your data: visions, milestones,
                  actions, documents, habits, todos, and journal. This cannot be undone.
                </p>
              </div>
              <button type="button" className="acc-delete-btn" onClick={() => { setShowDelete(true); setConfirmText(''); }}>
                Delete account
              </button>
            </div>
          </section>
        </div>
      </div>

      {showDelete && (
        <div className="acc-modal-overlay" onMouseDown={() => !deleting && setShowDelete(false)}>
          <div className="acc-modal" onMouseDown={e => e.stopPropagation()}>
            <h3 className="acc-modal-title">Delete your account?</h3>
            <p className="acc-modal-text">
              This permanently erases your account and everything in it. There is no undo.
              Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              className="acc-modal-input"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
            />
            <div className="acc-modal-actions">
              <button className="acc-modal-cancel" onClick={() => setShowDelete(false)} disabled={deleting}>Cancel</button>
              <button
                className="acc-modal-go"
                onClick={handleDelete}
                disabled={confirmText.trim().toUpperCase() !== 'DELETE' || deleting}
              >
                {deleting ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Account;
